/**
 * Route-Auflösung — 1:1 zur bestehenden Logik in reactApp.jsx / app-loader.js.
 *
 * Später: Aufrufer auf diese Hilfen umstellen (Single Source), Verhalten unverändert lassen.
 *
 * - resolveAppKeyFromRoute → entspricht reactApp.jsx __appKeyForRoute (Feature-Flags / Trainer-Guard)
 * - resolveLoaderAppKeyFromRoute → entspricht modules/app-loader.js resolveAppKeyForRoute (DynamicAppLoader)
 */

function __asStr(v) {
  return String(v ?? "").trim();
}

/** Feature-Flags in DB nutzen weiterhin den Key „schleifchen“. */
export const MATCHMIXER_FEATURE_FLAG_KEY = "schleifchen";

function __isMatchmixerRouteKey(r) {
  return (
    r === "hub-matchmixer" ||
    r.startsWith("hub-matchmixer-") ||
    r === "matchmixer-anmelden" ||
    r === "hub-schleifchen" ||
    r === "matchmixer" ||
    r.startsWith("matchmixer-") ||
    r === "schleifchen" ||
    r.startsWith("schleifchen-")
  );
}

/**
 * Spiegelt reactApp.jsx __appKeyForRoute — Reihenfolge und Bedingungen unverändert.
 * @param {string} routeOrPath
 * @returns {string|null}
 */
export function resolveAppKeyFromRoute(routeOrPath) {
  const r = __asStr(routeOrPath);
  if (!r) return null;
  if (__isMatchmixerRouteKey(r)) return "matchmixer";
  if (
    r === "hub-clubmeisterschaft" ||
    r.startsWith("hub-clubmeisterschaft-") ||
    r === "clubmeisterschaft" ||
    r.startsWith("clubmeisterschaft-")
  ) {
    return "clubmeisterschaft";
  }
  if (r === "medenspiele" || r.startsWith("medenspiele-")) return "medenspiele";
  if (r === "belegung" || r.startsWith("belegung-") || r === "hub-belegung")
    return "belegung";
  if (r === "kalender" || r.startsWith("kalender-") || r === "hub-kalender")
    return "kalender";
  if (
    r === "insights" ||
    r.startsWith("insights-") ||
    r === "hub-insights" ||
    r === "statistik"
  ) {
    return "insights";
  }
  if (
    r === "matchflow" ||
    r.startsWith("matchflow-") ||
    r === "hub-matchflow" ||
    r === "matchflow-accept"
  ) {
    return "matchflow";
  }
  if (
    r === "winterdoppel" ||
    r.startsWith("winterdoppel-") ||
    r === "hub-winterdoppel" ||
    r === "serientermine" ||
    r.startsWith("serientermine-") ||
    r === "hub-serientermine" ||
    r === "regeltermine" ||
    r.startsWith("regeltermine-") ||
    r === "hub-regeltermine"
  )
    return "winterdoppel";
  if (r === "minigames" || r.startsWith("minigames-")) return "minigames";
  if (
    r === "tennis-live" ||
    r.startsWith("tennis-live-") ||
    r === "tenniswelt" ||
    r.startsWith("tenniswelt-") ||
    r === "hub-tour"
  )
    return "tennis-live";
  if (r === "trainerstunden" || r.startsWith("trainerstunden-"))
    return "trainerstunden";
  if (r === "trainingsdesk-invite") return "trainerstunden";
  if (r === "trainingsdesk" || r.startsWith("trainingsdesk-"))
    return "trainerstunden";
  if (r === "coachdesk" || r.startsWith("coachdesk-")) return "trainerstunden";
  if (r === "hub-trainingsdesk") return "trainerstunden";
  if (r === "hub-trainer") return "trainer";
  if (r === "trainer" || r.startsWith("trainer-")) return "trainer";
  return null;
}

/**
 * Spiegelt modules/app-loader.js resolveAppKeyForRoute (Deterministisches Loader-Mapping).
 * @param {string} routeKey
 * @returns {string|null}
 */
export function resolveLoaderAppKeyFromRoute(routeKey) {
  const r = String(routeKey || "").trim();
  if (!r) return null;
  if (__isMatchmixerRouteKey(r)) return "matchmixer";
  if (r.startsWith("clubmeisterschaft")) return "clubmeisterschaft";
  if (r.startsWith("trainerstunden")) return "trainerstunden";
  if (r === "trainingsdesk-invite") return "trainerstunden";
  if (r.startsWith("trainingsdesk")) return "trainerstunden";
  if (r === "coachdesk" || r.startsWith("coachdesk")) return "trainerstunden";
  if (r === "hub-trainer") return "trainer";
  if (r === "trainer" || r.startsWith("trainer-")) return "trainer";
  if (r.startsWith("medenspiele")) return "medenspiele";
  if (r === "belegung" || r.startsWith("belegung-") || r === "hub-belegung")
    return "belegung";
  if (r === "kalender" || r.startsWith("kalender-") || r === "hub-kalender")
    return "kalender";
  if (
    r === "insights" ||
    r.startsWith("insights-") ||
    r === "hub-insights" ||
    r === "statistik"
  ) {
    return "insights";
  }
  if (
    r === "matchflow" ||
    r.startsWith("matchflow-") ||
    r === "hub-matchflow" ||
    r === "matchflow-accept"
  ) {
    return "matchflow";
  }
  if (
    r === "winterdoppel" ||
    r.startsWith("winterdoppel-") ||
    r === "hub-winterdoppel" ||
    r === "serientermine" ||
    r.startsWith("serientermine-") ||
    r === "hub-serientermine" ||
    r === "regeltermine" ||
    r.startsWith("regeltermine-") ||
    r === "hub-regeltermine"
  )
    return "winterdoppel";
  if (r.startsWith("minigames")) return "minigames";
  if (r === "share" || r.startsWith("share-")) return "share";
  return null;
}

/**
 * Feature-Flag-Key für eine Route (IST: isFeatureEnabledSync(appKey) mit appKey aus __appKeyForRoute).
 * @param {string} routeOrPath
 * @param {{ getEntry?: (k: string) => object|null }} [catalog]
 * @returns {string|null}
 */
export function getFeatureFlagKeyForRoute(routeOrPath, catalog = null) {
  const appKey = resolveAppKeyFromRoute(routeOrPath);
  if (!appKey) return null;
  if (catalog && typeof catalog.getEntry === "function") {
    const entry = catalog.getEntry(appKey);
    if (entry && typeof entry.featureFlagKey === "string")
      return entry.featureFlagKey;
  }
  if (appKey === "matchmixer") return MATCHMIXER_FEATURE_FLAG_KEY;
  return appKey;
}
